import { Row, RowItemType, Section, SectionType, SelectorIdentifier, UIElementType, UIItem } from '../../models';
import { calculateMaintenanceInterval, convertDate, createSmartTranslationMessage } from '../../models/utils';
import { MonitorSeriesNames } from '../../../location-card.constants';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { LocationStatus, RainLocation } from 'app/shared/models/locations';
import { LocationCardMonitorSeries } from 'app/shared/models/monitorsetting';
import { pipeInstallationConfig } from './installation-types/pipe';
import { weirInstallationConfig } from './installation-types/weir';
import { flumeInstallationConfig } from './installation-types/flume';
import { EntityResponseItem } from 'app/shared/models/view-data';
import { InstallationSeries, InstallationType } from 'app/shared/models/location-details';
import {
    enableField,
    loadInstallationTypes,
    setGlobalConfig,
    removeSection,
    pickMapCoords,
    statusChange,
    attachSectionToRow,
    setFieldValueAction,
    markFormDirty,
    evaluateFlexScanModeLock,
} from '../../actions';
import { FlexInfoFieldId, FlexSectionIds } from './base-config-models';
import { LocationCardStatus, SampleRateElement } from '../../../models/location-card.model';
import { QCONTINUITY_ENTITY } from 'app/shared/constant';
import { ConfigureAlarmsFieldId, InstallationTypeSectionIds } from '../common.models';
import { BasicInfoCommonFieldIds } from '../common.models';
import { ValidatorIdentifier } from '../../models/validator-identifier';
import { field, FieldActions } from '../../core/field-schema';
import { required, maxLength, pattern, custom } from '../../core/validator-builders';
import { BASE_MONITOR_SERIES } from '../../constants/base';
import { FlexLocationCardState } from './models';
import { TextField } from '../../models/field-models';
import { Action } from '@ngrx/store';
import { FlexCreateMode } from '../../reducers';
import { createFlexScanModeField, resolveFlexScanMode, shouldLockFlexScanRate } from './scan-mode';

export function getFlexCreateModeElement(mode: FlexCreateMode): UIItem {
    switch (mode) {
        case FlexCreateMode.init:
            return { ...CreateFlexInfoTextElement };
        case FlexCreateMode.wait:
            return {
                ...CreateFlexWaitTextElement,
                panelClass: 'input-text flex-wait-state',
            };
        case FlexCreateMode.error:
            return { ...CreateFlexErrorTextElement };
        default:
            return { ...CreateFlexInfoTextElement };
    }
}

export const CreateFlexWaitTextElement: UIItem = {
    id: FlexInfoFieldId.CreateFlexWaitText,
    type: RowItemType.UI,
    uiType: UIElementType.Text,
    label: 'ADD_EDIT.FLEX_CREATE_WAIT',
    panelClass: 'input-text flex-wait-state',
};

export const CreateFlexInfoTextElement: UIItem = {
    id: FlexInfoFieldId.CreateFlexInfoText,
    type: RowItemType.UI,
    uiType: UIElementType.Text,
    label: 'ADD_EDIT.FLEX_CREATE_WARNING',
    panelClass: 'input-text flex-warning-message',
};

export const CreateFlexErrorTextElement: UIItem = {
    id: FlexInfoFieldId.CreateFlexErrorText,
    type: RowItemType.UI,
    uiType: UIElementType.Text,
    label: 'ADD_EDIT.FLEX_CREATE_FAIL',
    panelClass: 'input-text flex-warning-message',
};

export const CreateFlexTextRow = (child: UIItem): Row => ({
    info: {
        id: FlexInfoFieldId.CreateFlexTextRow,
        panelClass: 'custom-50p flex flex-center m-5',
    },
    items: [child],
});

export const customPropertyRowGenerator = (index: number, populateValues?: { key: string; value: string }): Row => {
    return {
        info: {
            id: `custom-property-${index}`,
            panelClass: 'custom-50p',
        },
        items: [
            field.autocomplete<string, string>(`${BasicInfoCommonFieldIds.CustomPropertyKey}-${index}`, 'key', {
                optionsSelector: SelectorIdentifier.TagKeyAutocomplete,
                value: populateValues?.key,
                placeholder: 'ADD_EDIT.INPUT_TAG_NAME',
                validators: [
                    required(),
                    maxLength({ value: 128 }),
                    custom({
                        identifier: ValidatorIdentifier.CustomPropUniqueKey,
                        message: 'ADD_EDIT.DUPLICATE_TAG_NAME_ERROR',
                    }),
                ],
                panelClass: 'width-90',
                clearable: false,
                autoClearOnFocus: false,
                displayMapper: (option) => option,
                valueMapper: (option) => option,
            }),
            field.text(`${BasicInfoCommonFieldIds.CustomPropertyValue}-${index}`, 'value', {
                value: populateValues?.value,
                placeholder: 'ADD_EDIT.INPUT_TAG_VALUE',
                validators: [required(), maxLength({ value: 128 })],
                panelClass: 'width-90',
            }),
        ],
    };
};

/**
 * FLEX form configuration
 */
export const basicInfo = (state: FlexLocationCardState): Section => {
    const { locationCardData } = state;
    const { editMode } = locationCardData;

    return editMode ? editModeConfig(state) : createModeConfig();
};

const editModeConfig = (state: FlexLocationCardState): Section => {
    const settings = state.settings;
    const payload = state.locationDetails && state.locationDetails.length ? state.locationDetails[0] : null;
    const monitorConfig = state.monitorConfiguration;

    const rainGuage =
        payload && payload.assignedRainGaugeLocationId
            ? state.options.rainGauges.data.find((v) => v.locationId === payload.assignedRainGaugeLocationId)
            : null;
    const isEditMode = !!payload;

    const isSwitchMonitor = payload && payload.series.toLowerCase() !== MonitorSeriesNames.flex.toLowerCase();
    const scanMode = resolveFlexScanMode(monitorConfig?.scanMode);
    const isScanRateLocked =
        isEditMode &&
        !isSwitchMonitor &&
        shouldLockFlexScanRate(monitorConfig?.normalStorageInterval, monitorConfig?.fastStorageInterval);
    const showCoordsButton = state.locationCardData.enableSelectOnMap;

    const basicInfoConfig: Section = {
        info: {
            id: FlexSectionIds.BasicInfo,
            title: 'ADD_EDIT.ADD_EDIT_INFORMATION',
            displayTitle: true,
            expanded: true,
            expandable: true,
            panelClass: FlexSectionIds.BasicInfo,
        },
        rows: [
            // Row 1: Location Name and Series
            {
                info: {
                    id: 'basic-info-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.LocationName, 'locationName', {
                        value: isEditMode ? payload.locationName : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_NAME',
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            custom({
                                identifier: ValidatorIdentifier.UniqueLocationName,
                                message: 'COMMON.LOCATION_NAME_EXISTS',
                            }),
                            maxLength({
                                value: 63,
                            }),
                        ],
                        events: {
                            onChange: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.trim().charAt(0).toUpperCase() + value.trim().slice(1);
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    field.select<LocationCardMonitorSeries, string>(BasicInfoCommonFieldIds.Series, 'series', {
                        panelClass: 'custom-width',
                        value: BASE_MONITOR_SERIES.find(
                            (v) => v.value.toLowerCase() === MonitorSeriesNames.flex.toLowerCase(),
                        )?.value,
                        placeholder: 'COMMON.MONITOR_SERIES_PLACEHOLDER',
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.MonitorSeriesOptions,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        events: {
                            onChange: (value) => [
                                enableField({ fieldId: BasicInfoCommonFieldIds.InstallationType }),
                                loadInstallationTypes({ installationId: value.monitorSeriesID }),
                                setGlobalConfig({ monitorSeriesName: value.value }),
                            ],
                        },
                    }),
                ],
            },
            // Row 2: Serial Number and Status
            {
                info: {
                    id: 'basic-info-row-2',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.SerialNumber, 'serialnumber', {
                        panelClass: 'custom-width',
                        value: isEditMode ? payload.serialNumber : null,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SERIAL',
                        validators: [
                            required(),
                            maxLength({ value: 63 }),
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWith_Pattern,
                                message: 'HOME.MAP.MARKER_LOCATION_DETAIL.SERIAL_NUMBER_ERROR',
                            }),
                        ],
                        events: {
                            onInput: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.replace(/[^0-9]/g, '');
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    {
                        id: BasicInfoCommonFieldIds.StatusSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: 'status-container',
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'status-group custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'status-row',
                                        panelClass: 'status-inner-row flex align-items-center gap-4',
                                    },
                                    items: [
                                        field.select<LocationCardStatus, number>(
                                            BasicInfoCommonFieldIds.Status,
                                            'status',
                                            {
                                                value: isEditMode ? payload.status : 0,
                                                panelClass: 'custom-width',
                                                disabled: !isEditMode,
                                                label: 'Status',
                                                valueMapper: (option) => option.key,
                                                displayMapper: (option) => option.name,
                                                validators: [required()],
                                                optionsSelector: SelectorIdentifier.LocationStatuses,
                                                events: {
                                                    onChange: () => [statusChange({ isMp1: true })],
                                                },
                                            },
                                        ),
                                        field.select<number, number>(
                                            BasicInfoCommonFieldIds.MaintenanceInterval,
                                            'maintenanceInterval',
                                            {
                                                value: isEditMode
                                                    ? calculateMaintenanceInterval(payload.maintenanceEnd)
                                                    : 2,
                                                label: 'CUSTOMER_EDITOR.HOURS',
                                                valueMapper: (option) => option,
                                                displayMapper: (option) => String(option),
                                                panelClass: 'width-15',
                                                visible: isEditMode
                                                    ? payload.status === LocationStatus.Maintenance
                                                    : false,
                                                optionsSelector: SelectorIdentifier.MaintenanceIntervals,
                                            },
                                        ),
                                        {
                                            id: BasicInfoCommonFieldIds.MaintenanceEnd,
                                            name: 'maintenanceEnd',
                                            panelClass: 'width-25 field-status-info',
                                            label: isEditMode
                                                ? createSmartTranslationMessage(
                                                      'LOCATION_CARD_DYNAMIC_TRANSLATION.RETURN_TO_ACTIVE',
                                                      {
                                                          date: convertDate(payload.maintenanceEnd, state.customer?.dateFormat, state.customer?.timeFormat),
                                                      },
                                                  )
                                                : 'CUSTOMER_EDITOR.HOURS',
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Text,
                                            visible: isEditMode ? payload.status === LocationStatus.Maintenance : false,
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                ],
            },
            // Row 4: APN Provider and Description
            {
                info: {
                    id: 'basic-info-row-5',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.Description, 'description', {
                        value: isEditMode ? payload.description : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LOCATION',
                        validators: [
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            maxLength({
                                value: 256,
                            }),
                        ],
                        events: {
                            onChange: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.charAt(0).toUpperCase() + value.slice(1);
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                ],
            },

            // Row 5: Rain Gauge and Manhole Depth
            {
                info: {
                    id: 'location-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.autocomplete<RainLocation, string>(
                        BasicInfoCommonFieldIds.AssignedRainGauge,
                        'assignedRainGauge',
                        {
                            value: rainGuage ? rainGuage.locationName : null,
                            autoClearOnFocus: true,
                            panelClass: 'custom-width',
                            label: 'Assigned Rain Gauge',
                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_ASSIGNED',
                            clearable: true,
                            optionsSelector: SelectorIdentifier.RainGaugeAutocomplete,
                            displayMapper: (option) => option.locationName,
                            valueMapper: (option) => option.locationName,
                            validators: [
                                maxLength({
                                    value: 50,
                                }),
                            ],
                        },
                    ),
                    field.number(BasicInfoCommonFieldIds.ManholeDepth, 'manholedepth', {
                        value: isEditMode ? payload.manholeDepth : null,
                        panelClass: 'custom-width',
                        placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.MANHOLE_DEPTH',
                        placeholderSuffix: settings.depthUnit,
                        validators: [
                            pattern({
                                value: settings.isMetric
                                    ? REGEX_CONFIG.numeric0_100000With1DecimalPlacesPattern
                                    : REGEX_CONFIG.numeric0_100000With2DecimalPlacesPattern,
                                message: settings.isMetric
                                    ? 'COMMON.NUMERIC_PRECISION_ERROR_METRIC'
                                    : 'COMMON.NUMERIC_PRECISION_ERROR',
                            }),
                            maxLength({
                                value: 50,
                            }),
                            custom({
                                identifier: ValidatorIdentifier.ManholeDepthToPipeHeightValidator,
                                message: 'COMMON.MAN_HOLE_DEPTH_PIPE_HEIGHT_ERROR',
                            }),
                        ],
                    }),
                ],
            },

            // Row 6: Address
            {
                info: {
                    id: 'location-row-2',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.ManholeAddress, 'manholeaddress', {
                        value: isEditMode ? payload.manholeAddress : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MANHOLE_ADDRESS',
                        validators: [
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            maxLength({
                                value: 64,
                            }),
                        ],
                        events: {
                            onChange: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.charAt(0).toUpperCase() + value.slice(1);
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    {
                        id: BasicInfoCommonFieldIds.CoordinatesSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: BasicInfoCommonFieldIds.CoordinatesSection,
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'coordinates-group custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'coordinates-row',
                                        panelClass: `coordinates-inner-row ${
                                            showCoordsButton ? 'flex-end' : 'justify-space-between'
                                        }`,
                                    },
                                    items: [
                                        field.number(BasicInfoCommonFieldIds.Latitude, 'latitude', {
                                            value: isEditMode ? payload.latitude : 0,
                                            panelClass: 'custom-width',
                                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LATITUDE',
                                            validators: [
                                                pattern({
                                                    value: REGEX_CONFIG.latitudePattern,
                                                    message: 'COMMON.LONGITUDE_ERROR',
                                                }),
                                            ],
                                        }),
                                        field.number(BasicInfoCommonFieldIds.Longitude, 'longitude', {
                                            value: isEditMode ? payload.longitude : 0,
                                            panelClass: 'custom-width',
                                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LONGITUDE',
                                            validators: [
                                                pattern({
                                                    value: REGEX_CONFIG.longitudePattern,
                                                    message: 'COMMON.LONGITUDE_ERROR',
                                                }),
                                            ],
                                        }),
                                        {
                                            id: 'move-location-icon',
                                            name: 'moveLocationIcon',
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Image,
                                            visible: !!showCoordsButton,
                                            imgSrc: '/assets/images/icons/move_icon.png',
                                            imgHeight: 18,
                                            imgWidth: 18,
                                            panelClass: 'move-icon',
                                            events: {
                                                onClick: () => [pickMapCoords()],
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                ],
            },

            // Row 7: Sample Rates
            {
                info: {
                    id: 'settings-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<SampleRateElement, number>(BasicInfoCommonFieldIds.SampleRate, 'samplerate', {
                        value: isEditMode && monitorConfig ? monitorConfig.normalStorageInterval : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SAMPLE_RATE',
                        validators: [required(), custom({ identifier: ValidatorIdentifier.NormalRateValidator })],
                        optionsSelector: SelectorIdentifier.FlexSampleRates,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        events: {
                            onChange: () => [evaluateFlexScanModeLock()],
                        },
                    }),
                    field.select<SampleRateElement, number>(BasicInfoCommonFieldIds.FastRate, 'fastrate', {
                        value: isEditMode && monitorConfig ? monitorConfig.fastStorageInterval : null,
                        panelClass: 'custom-width',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_FAST_SAMPLE_RATE',
                        validators: [
                            required(),
                            custom({
                                identifier: ValidatorIdentifier.FastRateValidator,
                                message: 'COMMON.TRITON_SAMPLE_RATE_ERROR',
                            }),
                        ],
                        optionsSelector: SelectorIdentifier.FlexSampleRates,
                        events: {
                            onChange: () => [evaluateFlexScanModeLock()],
                        },
                    }),
                ],
            },
            {
                info: {
                    id: 'scan-rate-row',
                    panelClass: 'custom-50p',
                },
                items: [createFlexScanModeField(scanMode, isScanRateLocked)],
            },
            // Row 9: Installation Date
            {
                info: {
                    id: 'settings-row-3',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.date(BasicInfoCommonFieldIds.InstallationDate, 'installationDate', {
                        label: 'Installation Date',
                        placeholder: 'Select date',
                        dateFormat: settings.dateFormat,
                        datePickerType: 'date',
                        timeInterval: 1,
                        panelClass: 'full-width',
                        value: isEditMode ? payload.installationDate : new Date(),
                        events: {
                            onChange: () => [markFormDirty()],
                        }
                    }),
                ],
            },

            // Row 10: Installation Type and QFinal
            {
                info: {
                    id: 'installation-type-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<InstallationType, string>(
                        BasicInfoCommonFieldIds.InstallationType,
                        'installationType',
                        {
                            preserveOldValues: false,
                            value:
                                isEditMode && !isSwitchMonitor
                                    ? `${payload.installationType}: ${payload.installationShapeTypeID}`
                                    : '',
                            placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.INSTALLATION_TYPE',
                            validators: [required()],
                            optionsSelector: SelectorIdentifier.FlexInstallationTypes,
                            panelClass: 'custom-width',
                            valueMapper: (option) => option.value,
                            displayMapper: (option) => option.text,
                            events: {
                                onChange: (value) => {
                                    const toDispatch: Action[] = [
                                        setFieldValueAction({
                                            fieldId: ConfigureAlarmsFieldId.FullPipeThreshold,
                                            value: null,
                                        }),
                                    ];
                                    const installationSections = [
                                        InstallationTypeSectionIds.Pipe,
                                        InstallationTypeSectionIds.Weir,
                                        InstallationTypeSectionIds.Flume,
                                    ];

                                    installationSections.forEach((sectionId) => {
                                        toDispatch.push(
                                            removeSection({ sectionId, parentSectionId: FlexSectionIds.BasicInfo }),
                                        );
                                    });
                                    // For Pipe type (Series 200)
                                    if (value.series === InstallationSeries.Pipe) {
                                        toDispatch.push(
                                            attachSectionToRow({
                                                targetSectionId: FlexSectionIds.BasicInfo, // The parent section ID
                                                rowId: 'installation-type-row',
                                                fieldId: InstallationTypeSectionIds.Pipe,
                                                sectionConfig: pipeInstallationConfig(state, true),
                                            }),
                                        );
                                    } else if (value.series === InstallationSeries.Weir) {
                                        toDispatch.push(
                                            attachSectionToRow({
                                                targetSectionId: FlexSectionIds.BasicInfo,
                                                rowId: 'installation-type-row',
                                                fieldId: InstallationTypeSectionIds.Weir,
                                                sectionConfig: weirInstallationConfig(state, true),
                                            }),
                                        );
                                    } else if (value.series === InstallationSeries.Flume) {
                                        toDispatch.push(
                                            attachSectionToRow({
                                                targetSectionId: FlexSectionIds.BasicInfo,
                                                rowId: 'installation-type-row',
                                                fieldId: InstallationTypeSectionIds.Flume,
                                                sectionConfig: flumeInstallationConfig(state, true),
                                            }),
                                        );
                                    }

                                    return toDispatch;
                                },
                            },
                        },
                    ),
                    field.select<EntityResponseItem, number>(BasicInfoCommonFieldIds.QFinalEntityID, 'qFinalEntityID', {
                        value: isEditMode ? payload.qFinalEntityID : QCONTINUITY_ENTITY,
                        placeholder: 'COMMON.QFINALTEXT',
                        panelClass: 'custom-width',
                        optionsSelector: SelectorIdentifier.QFinalEntities,
                        valueMapper: (option) => option.entityId,
                        displayMapper: (option) => option.entityName,
                    }),
                ],
            },
            // Custom properties section
            {
                info: {
                    id: 'custom-properties-container-row',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: BasicInfoCommonFieldIds.CustomPropertiesSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: BasicInfoCommonFieldIds.CustomPropertiesSection,
                                title: 'ADD_EDIT.TAGS_SECTION_NAME',
                                displayTitle: false,
                                displayActionBtns: true,
                                type: SectionType.Array,
                                expanded: false,
                                expandable: false,
                                itemGenerator: customPropertyRowGenerator,
                                panelClass: 'custom-properties-panel',
                            },
                            rows:
                                isEditMode && payload.customProperties
                                    ? payload.customProperties.map((item, index) =>
                                          customPropertyRowGenerator(index, item),
                                      )
                                    : [],
                        },
                    },
                ],
            },
        ],
    };

    if (!isEditMode || isSwitchMonitor) {
        return basicInfoConfig;
    }

    const installationConfigGenerator = pickInstallationTypeConfig(payload.installationShapeTypeID);

    if (!installationConfigGenerator) {
        return basicInfoConfig;
    }

    const installationConfig = installationConfigGenerator(state, true);
    const sectionId = getInstallationTypeSectionId(payload.installationShapeTypeID);

    const installationTypeRowIndex = basicInfoConfig.rows.findIndex((row) => row.info.id === 'installation-type-row');
    basicInfoConfig.rows.splice(installationTypeRowIndex + 1, 0, {
        info: { id: sectionId, panelClass: 'full-width' },
        items: [{ id: sectionId, type: RowItemType.Section, section: installationConfig }],
    });

    return basicInfoConfig;
};

const createModeConfig = (): Section => {
    return {
        info: {
            id: FlexSectionIds.BasicInfo,
            title: 'ADD_EDIT.ADD_EDIT_INFORMATION',
            displayTitle: true,
            expanded: true,
            expandable: true,
            panelClass: FlexSectionIds.BasicInfo,
        },
        rows: [
            {
                info: {
                    id: 'basic-info-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.SerialNumber, 'serialnumber', {
                        panelClass: 'custom-width',
                        value: null,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SERIAL',
                        validators: [
                            required(),
                            maxLength({ value: 63 }),
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWith_Pattern,
                                message: 'HOME.MAP.MARKER_LOCATION_DETAIL.SERIAL_NUMBER_ERROR',
                            }),
                        ],
                        events: {
                            onInput: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.replace(/[^0-9]/g, '');
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    field.select<LocationCardMonitorSeries, string>(BasicInfoCommonFieldIds.Series, 'series', {
                        panelClass: 'custom-width',
                        value: BASE_MONITOR_SERIES.find(
                            (v) => v.value.toLowerCase() === MonitorSeriesNames.flex.toLowerCase(),
                        )?.value,
                        placeholder: 'COMMON.MONITOR_SERIES_PLACEHOLDER',
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.MonitorSeriesOptions,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        events: {
                            onChange: (value) => [
                                enableField({ fieldId: BasicInfoCommonFieldIds.InstallationType }),
                                loadInstallationTypes({ installationId: value.monitorSeriesID }),
                                setGlobalConfig({ monitorSeriesName: value.value }),
                            ],
                        },
                    }),
                ],
            },
            CreateFlexTextRow(getFlexCreateModeElement(FlexCreateMode.init)),
        ],
    };
};

function pickInstallationTypeConfig(installationSeries: number) {
    if (installationSeries >= InstallationSeries.Pipe && installationSeries < InstallationSeries.Weir) {
        return pipeInstallationConfig;
    } else if (installationSeries >= InstallationSeries.Weir && installationSeries < InstallationSeries.Flume) {
        return weirInstallationConfig;
    } else if (installationSeries >= InstallationSeries.Flume && installationSeries < InstallationSeries.Rain) {
        return flumeInstallationConfig;
    }
}

function getInstallationTypeSectionId(installationSeries: number) {
    if (installationSeries >= InstallationSeries.Pipe && installationSeries < InstallationSeries.Weir) {
        return InstallationTypeSectionIds.Pipe;
    } else if (installationSeries >= InstallationSeries.Weir && installationSeries < InstallationSeries.Flume) {
        return InstallationTypeSectionIds.Weir;
    } else if (installationSeries >= InstallationSeries.Flume && installationSeries < InstallationSeries.Rain) {
        return InstallationTypeSectionIds.Flume;
    }
}
